import React, { useCallback, useEffect, useState } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { Download, FileText, IndianRupee, X } from 'lucide-react';
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate } from '../../utils/localDate.js';
import {
  emptyStateClass,
  listIdLinkClass,
  listIdLinkMobileClass,
  listPageWrapperClass,
  mobileCardClass,
  tableBodyClass,
  tableCellClass,
  tableClass,
  tableHeadCellClass,
  tableHeadRowClass,
  tableRowClass,
} from '../common/uiClasses.js';

const compactControlClass =
  'w-full min-w-0 h-10 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white';

const StatTile = ({ label, value, tone = 'gray' }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    red: 'bg-red-50 text-red-700',
    purple: 'bg-purple-50 text-purple-700',
    gray: 'bg-gray-50 text-gray-800',
  };
  return (
    <div className={`rounded-xl px-2.5 py-2 min-w-0 ${tones[tone] || tones.gray}`}>
      <p className="text-[10px] sm:text-[11px] font-medium opacity-80">{label}</p>
      <p className="text-sm sm:text-base font-bold tabular-nums truncate">{value}</p>
    </div>
  );
};

const VendorBatchShipmentBill = () => {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [summary, setSummary] = useState({
    total_shipments: 0,
    total_amount: 0,
    paid_amount: 0,
    balance_amount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');

  const { errors, validate, validateField } = useValidation();
  const hasDateFilters = Boolean(fromDate || toDate);
  const dueAmount = Number(summary.balance_amount || 0);

  const paymentAmountRule = (v) => {
    const requiredErr = validators.required(v, 'Payment amount');
    if (requiredErr) return requiredErr;
    const numberErr = validators.number(v, { min: 0.01, label: 'Payment amount' });
    if (numberErr) return numberErr;
    if (Number(v) > dueAmount) {
      return `Payment amount cannot exceed due amount of ${fullCurrency(dueAmount)}`;
    }
    return '';
  };

  const validationRules = {
    paymentAmount: paymentAmountRule,
  };

  const dateParams = useCallback(() => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return params;
  }, [fromDate, toDate]);

  const fetchClientShipments = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await shipmentClientService.getClientShipments(clientId, dateParams());
      if (response.success) {
        setClient(response.client);
        setShipments(response.shipments);
        setSummary(response.summary);
      }
    } catch (err) {
      Alert.error('Failed to load client shipments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [clientId, dateParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClientShipments();
    }, hasDateFilters ? 250 : 0);
    return () => clearTimeout(timer);
  }, [fetchClientShipments, hasDateFilters]);

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      await shipmentClientService.exportClientShipments(clientId, dateParams());
      Alert.success('Excel file downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to export Excel file');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadStatement = async () => {
    try {
      setDownloading(true);
      await shipmentClientService.downloadClientInvoice(clientId, dateParams());
      Alert.success('Statement downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download statement');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handlePaymentAmountChange = (e) => {
    const filtered = inputFilters.amount(e.target.value);
    const wasOver = Number(paymentAmount) > dueAmount;
    setPaymentAmount(filtered);
    const error = validateField('paymentAmount', filtered, paymentAmountRule);
    if (error && Number(filtered) > dueAmount && !wasOver) {
      Alert.error(`Payment amount cannot exceed due amount of ${fullCurrency(dueAmount)}`);
    }
  };

  const handleBatchPayment = async (e) => {
    e.preventDefault();

    if (!validate({ paymentAmount }, validationRules)) return;

    if (parseFloat(paymentAmount) > summary.balance_amount) {
      Alert.error(`Payment amount cannot exceed due amount of ${fullCurrency(summary.balance_amount)}`);
      return;
    }

    const confirmed = await Alert.confirm(
      `Are you sure you want to process payment of ${fullCurrency(paymentAmount)}?`,
      'Confirm Payment'
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);
      const response = await shipmentClientService.batchPayment(clientId, {
        payment_amount: parseFloat(paymentAmount),
        payment_mode: paymentMode,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      });

      if (response.success) {
        Alert.success(response.message);
        setPaymentAmount('');
        setPaymentMode('cash');
        fetchClientShipments();
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to process payment');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentStatusBadge = (mode) => {
    const statusColors = {
      to_pay: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      paid_balance: 'bg-blue-100 text-blue-800',
      credit: 'bg-orange-100 text-orange-800',
    };
    return statusColors[mode] || 'bg-gray-100 text-gray-800';
  };

  const formatPaymentMode = (mode) => {
    if (!mode) return '—';
    return mode.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const clientSubtitle = client
    ? [client.full_name, client.company_name].filter(Boolean).join(' · ')
    : '';

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
        <ListPageHeader
          title={t('pages.shipmentClient.batchBill')}
          subtitle={clientSubtitle}
          icon={IndianRupee}
          backTo="/shipment-clients"
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-secondary shrink-0"
          className="mb-3"
        >
          <Button
            variant="secondary"
            icon={FileText}
            mobileIconOnly
            onClick={handleDownloadStatement}
            disabled={downloading}
            loading={downloading}
            title="Download Statement"
          >
            {downloading ? 'Downloading...' : 'Download Statement'}
          </Button>
          <Button
            variant="success"
            icon={Download}
            mobileIconOnly
            onClick={handleExportExcel}
            disabled={exporting || shipments.length === 0}
            loading={exporting}
            title="Export Excel"
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button variant="secondary" className="hidden md:inline-flex" onClick={() => navigate('/shipment-clients')}>
            Back
          </Button>
        </ListPageHeader>

        <div className="bg-white rounded-xl shadow-sm p-2.5 sm:p-3 mb-3">
          <div className="grid grid-cols-2 md:flex md:flex-wrap md:items-end gap-2">
            <div className="min-w-0 md:w-40">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={compactControlClass}
              />
            </div>
            <div className="min-w-0 md:w-40">
              <label className="block text-[11px] font-medium text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={compactControlClass}
              />
            </div>
            {hasDateFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-1 h-10 px-3 rounded-xl text-sm text-gray-600 hover:bg-gray-50 border border-gray-200"
                title="Clear dates"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {client && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
            <StatTile label="Total Amount" value={fullCurrency(client.total_amount)} tone="blue" />
            <StatTile label="Paid Amount" value={fullCurrency(client.paid_amount)} tone="green" />
            <StatTile label="Due Amount" value={fullCurrency(client.due_amount)} tone="red" />
            <StatTile label="Shipments" value={summary.total_shipments} tone="purple" />
          </div>
        )}

        {summary.balance_amount > 0 && (
          <form onSubmit={handleBatchPayment} className="bg-white rounded-xl shadow-sm p-2.5 sm:p-3 mb-3">
            <div className="grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 md:gap-3 md:items-end">
              <div className="min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">
                  Amount (₹) <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal"> · max {fullCurrency(summary.balance_amount)}</span>
                </label>
                <input
                  type="text"
                  value={paymentAmount}
                  onChange={handlePaymentAmountChange}
                  placeholder="0.00"
                  className={`${compactControlClass} ${errors.paymentAmount ? 'border-red-400' : ''}`}
                  required
                />
                <ValidationMessage error={errors.paymentAmount} />
              </div>
              <div className="min-w-0">
                <label className="block text-[11px] font-medium text-gray-500 mb-1">
                  Mode <span className="text-red-500">*</span>
                </label>
                <SelectField
                  variant="form"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-full"
                  triggerClassName={compactControlClass}
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online</option>
                </SelectField>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Button
                  variant="success"
                  type="submit"
                  icon={IndianRupee}
                  loading={submitting}
                  disabled={submitting || !paymentAmount || Boolean(errors.paymentAmount) || Number(paymentAmount) > dueAmount}
                  className="w-full md:w-auto h-10 !px-4"
                >
                  {submitting ? 'Processing...' : 'Process Payment'}
                </Button>
              </div>
            </div>
          </form>
        )}

        <div className="bg-white rounded-xl shadow-sm">
          <div className="px-3 py-2.5 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-800">Shipment Details</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader fullScreen={false} size="default" message="Loading shipments..." />
            </div>
          ) : shipments.length === 0 ? (
            <div className={`${emptyStateClass} shadow-none py-8`}>No shipments found for the selected dates</div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className={`${tableClass} min-w-full`}>
                  <thead>
                    <tr className={tableHeadRowClass}>
                      <th className={tableHeadCellClass}>Shipment ID</th>
                      <th className={tableHeadCellClass}>Date</th>
                      <th className={tableHeadCellClass}>Sender</th>
                      <th className={tableHeadCellClass}>Receiver</th>
                      <th className={tableHeadCellClass}>Status</th>
                      <th className={`${tableHeadCellClass} text-right`}>Total</th>
                      <th className={`${tableHeadCellClass} text-right`}>Paid</th>
                      <th className={`${tableHeadCellClass} text-right`}>Balance</th>
                      <th className={tableHeadCellClass}>Payment</th>
                    </tr>
                  </thead>
                  <tbody className={tableBodyClass}>
                    {shipments.map((shipment) => (
                      <tr key={shipment.shipment_id} className={tableRowClass}>
                        <td className={tableCellClass}>
                          <Button
                            variant="link"
                            onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                            className={listIdLinkClass}
                          >
                            {shipment.shipment_id}
                          </Button>
                        </td>
                        <td className={`${tableCellClass} text-sm text-gray-700`}>
                          {formatIndianDate(shipment.created_at)}
                        </td>
                        <td className={`${tableCellClass} text-sm text-gray-700`}>{shipment.sender_name}</td>
                        <td className={`${tableCellClass} text-sm text-gray-700`}>{shipment.receiver_name}</td>
                        <td className={tableCellClass}>
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {shipment.status}
                          </span>
                        </td>
                        <td className={`${tableCellClass} text-sm text-right font-medium tabular-nums`}>
                          {fullCurrency(shipment.total_amount)}
                        </td>
                        <td className={`${tableCellClass} text-sm text-right text-green-600 font-medium tabular-nums`}>
                          {fullCurrency(shipment.paid_amount)}
                        </td>
                        <td className={`${tableCellClass} text-sm text-right text-red-600 font-medium tabular-nums`}>
                          {fullCurrency(shipment.balance_amount)}
                        </td>
                        <td className={tableCellClass}>
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadge(shipment.mode_of_payment)}`}>
                            {formatPaymentMode(shipment.mode_of_payment)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden p-2.5 space-y-2.5">
                {shipments.map((shipment) => (
                  <div key={shipment.shipment_id} className={`${mobileCardClass} p-3`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                        className={listIdLinkMobileClass}
                      >
                        {shipment.shipment_id}
                      </button>
                      <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${getPaymentStatusBadge(shipment.mode_of_payment)}`}>
                        {formatPaymentMode(shipment.mode_of_payment)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1.5">
                      {formatIndianDate(shipment.created_at)} · {shipment.status}
                    </p>
                    <p className="text-xs text-gray-600 truncate mb-2">
                      {shipment.sender_name} → {shipment.receiver_name}
                    </p>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="rounded-xl bg-blue-50 px-2 py-1.5 min-w-0">
                        <p className="text-[10px] text-blue-700/80">Total</p>
                        <p className="text-xs font-semibold text-blue-700 tabular-nums truncate">{fullCurrency(shipment.total_amount)}</p>
                      </div>
                      <div className="rounded-xl bg-green-50 px-2 py-1.5 min-w-0">
                        <p className="text-[10px] text-green-700/80">Paid</p>
                        <p className="text-xs font-semibold text-green-700 tabular-nums truncate">{fullCurrency(shipment.paid_amount)}</p>
                      </div>
                      <div className="rounded-xl bg-red-50 px-2 py-1.5 min-w-0">
                        <p className="text-[10px] text-red-700/80">Due</p>
                        <p className="text-xs font-bold text-red-600 tabular-nums truncate">{fullCurrency(shipment.balance_amount)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VendorBatchShipmentBill;
